import PropTypes from "prop-types";
import React from "react";
import ReactOnRails from "react-on-rails";
import ReCAPTCHA from "react-google-recaptcha";

import Loading from "../Loading";
import * as api from "../../utils/api";

// Rethink S1: the one signup flow. Same questions whichever way the account is
// made -- game (pre-filled from the page), platform, when you play, age range --
// and only the last step differs: "Continue with Discord" (a plain form POST, so the
// answers are held server-side through the OAuth round trip) or email and
// password. A Discord account created without answers (logged in, currentUserId
// set) gets the same questions with each answer saved as it's given.

const PLATFORMS = [
  { label: "PC", value: "pc" },
  { label: "PS5", value: "ps5" },
  { label: "PS4", value: "ps4" },
  { label: "Xbox Series X|S", value: "xbox-series" },
  { label: "Xbox One", value: "xbox-one" },
  { label: "Switch", value: "nintendo-switch" },
];

const SCHEDULES = [
  { value: "Weekday Mornings and Weekends", label: "Weekday mornings and weekends" },
  { value: "Weekday Afternoons and Weekends", label: "Weekday afternoons and weekends" },
  { value: "Weekday Evenings and Weekends", label: "Weekday evenings and weekends" },
  { value: "Weekdays Latenight and Weekends", label: "Late nights and weekends" },
];

// Stored as the midpoints the old signup form used, so matchmaking's age band
// (Group.adjusted_age_search) and group average age keep working. Kept hidden
// on the profile (hide_age) unless the user chooses to show it.
const AGE_RANGES = [
  { value: 15, label: "13–17" },
  { value: 25, label: "18–29" },
  { value: 35, label: "30–39" },
  { value: 45, label: "40–49" },
  { value: 55, label: "50–59" },
  { value: 65, label: "60–69" },
  { value: 75, label: "70+" },
];
const UNDER_18 = 15;

const GENERIC_ERROR = "Something went wrong. Check your details and try again.";
const MAX_GAME_RESULTS = 12;

// axios rejects on 409/422, so the server's own message ("you already have an
// account, try logging in") only ever reaches the .catch.
function serverError(e) {
  return (e && e.response && e.response.data && e.response.data.error) || GENERIC_ERROR;
}

function browserTimeZone() {
  return typeof window !== "undefined" && window.newTimeZone ? window.newTimeZone : "";
}

function labelFor(list, value) {
  const item = list.find((entry) => entry.value === value);
  return item ? item.label : value;
}

export default class SignupFlow extends React.Component {
  static propTypes = {
    authenticityToken: PropTypes.string,
    discordPath: PropTypes.string,
    game: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
    games: PropTypes.shape({ popular: PropTypes.array, all: PropTypes.array }),
    currentUserId: PropTypes.number,
    saved: PropTypes.shape({
      gameId: PropTypes.number,
      platform: PropTypes.string,
      playSchedule: PropTypes.string,
      age: PropTypes.number,
    }),
    joining: PropTypes.shape({ title: PropTypes.string, when: PropTypes.string }),
    groupName: PropTypes.string,
    discordOnly: PropTypes.bool,
    captchaKey: PropTypes.string,
  };

  constructor(props) {
    super(props);
    const saved = props.saved || {};
    const game = props.game || null;
    const platform = saved.platform || "";
    const playSchedule = saved.playSchedule || "";

    this.state = {
      game,
      platform,
      playSchedule,
      age: null,
      // The game question is part of the flow only when the page didn't
      // answer it (or the visitor asks to change it).
      askGame: !game,
      step: this.firstStep(game, platform, playSchedule),
      search: "",
      gamertag: "",
      email: "",
      password: "",
      termsAccepted: false,
      captchaToken: "",
      errors: "",
      loading: false,
    };
  }

  loggedIn = () => !!this.props.currentUserId;

  firstStep(game, platform, playSchedule) {
    if (!game) return "game";
    if (!this.loggedIn()) return "platform";
    if (!platform) return "platform";
    if (!playSchedule) return "schedule";
    return "age";
  }

  steps = () => {
    const questions = ["platform", "schedule", "age"];
    const steps = this.state.askGame ? ["game", ...questions] : questions;
    return this.loggedIn() ? steps : [...steps, "account"];
  };

  // Logged in (a Discord account made before the questions): save each answer
  // as it's given, so leaving halfway still keeps what they told us.
  // Fire-and-forget: the final save re-sends everything.
  saveStep = (fields) => {
    if (!this.loggedIn()) return;

    api
      .updateUser({
        headers: ReactOnRails.authenticityHeaders(),
        body: { user_id: this.props.currentUserId, ...fields },
      })
      .catch(() => {});
  };

  pickGame = (game) => {
    this.saveStep({ primary_game_id: game.id });
    this.setState({ game, search: "", step: "platform" });
  };

  pickPlatform = (platform) => {
    this.saveStep({ platform });
    this.setState({ platform, step: "schedule" });
  };

  pickSchedule = (playSchedule) => {
    this.saveStep({ play_schedule: playSchedule });
    this.setState({ playSchedule, step: "age" });
  };

  // The last question. Signed in (a Discord account made before the
  // questions), it finishes signup; otherwise the account step comes next.
  pickAge = (age) => {
    this.setState({ age }, () => {
      if (this.loggedIn()) {
        this.finishSignedIn();
      } else {
        this.setState({ step: "account" });
      }
    });
  };

  changeGame = (event) => {
    event.preventDefault();
    this.setState({ askGame: true, step: "game" });
  };

  goBack = () => {
    const steps = this.steps();
    const index = steps.indexOf(this.state.step);
    if (index > 0) this.setState({ step: steps[index - 1], errors: "" });
  };

  followRedirect = (response) => {
    if (response.data.redirect_url) {
      window.location.href = window.encodeURI(response.data.redirect_url);
    } else {
      this.setState({ loading: false, errors: response.data.error || GENERIC_ERROR });
    }
  };

  finishSignedIn = () => {
    this.setState({ loading: true, errors: "" });

    api
      .updateUser({
        headers: ReactOnRails.authenticityHeaders(),
        body: {
          user_id: this.props.currentUserId,
          primary_game_id: this.state.game.id,
          platform: this.state.platform,
          play_schedule: this.state.playSchedule,
          age: this.state.age,
          hide_age: true,
          time_zone: browserTimeZone() || undefined,
          tos_privacy_agreement: true,
          activate_group_mind: true,
        },
      })
      .then(this.followRedirect)
      .catch((e) => this.setState({ loading: false, errors: serverError(e) }));
  };

  emailReady = () =>
    this.state.gamertag && this.state.email && this.state.password.length >= 6 && this.state.termsAccepted;

  submitEmail = (event) => {
    event.preventDefault();
    if (!this.emailReady()) return;

    this.setState({ loading: true, errors: "" });

    api
      .createUser({
        headers: ReactOnRails.authenticityHeaders(),
        body: {
          gamertag: this.state.gamertag,
          email: this.state.email,
          password: this.state.password,
          platform: this.state.platform,
          play_schedule: this.state.playSchedule,
          play_style: "casual",
          age: this.state.age,
          hide_age: true,
          time_zone: browserTimeZone(),
          tos_privacy_agreement: true,
          primary_game_id: this.state.game.id,
          activate_group_mind: true,
          captcha_token: this.state.captchaToken,
          created_from: "website",
        },
      })
      .then(this.followRedirect)
      .catch((e) => this.setState({ loading: false, errors: serverError(e) }));
  };

  gameMatches() {
    const games = this.props.games || {};
    const query = this.state.search.trim().toLowerCase();
    if (!query) return games.popular && games.popular.length ? games.popular : (games.all || []).slice(0, MAX_GAME_RESULTS);

    return (games.all || []).filter((game) => game.name.toLowerCase().includes(query)).slice(0, MAX_GAME_RESULTS);
  }

  renderContext() {
    const { joining, groupName } = this.props;
    const steps = this.steps();
    const position = steps.indexOf(this.state.step) + 1;

    return (
      <div>
        {joining && (
          <div className="mb-3" data-signup-joining>
            <div className="small text-white-50">You're joining</div>
            <div className="lead text-white">{joining.title}</div>
            <div className="small text-white-50">{joining.when}</div>
          </div>
        )}
        {!joining && groupName && (
          <div className="mb-3" data-signup-joining>
            <div className="small text-white-50">You're joining</div>
            <div className="lead text-white">{groupName}</div>
          </div>
        )}
        <p className="small text-white-50 mb-2" data-signup-progress>
          Step {position} of {steps.length}
        </p>
      </div>
    );
  }

  renderIntro() {
    if (this.state.step !== this.steps()[0]) return null;
    if (this.loggedIn()) {
      return <p className="text-white-50 mb-4">Your account is ready. A few quick answers and we'll find your group.</p>;
    }
    if (this.props.joining) {
      return <p className="text-white-50 mb-4">A few quick answers, then you're in and your seat is saved.</p>;
    }
    return <p className="text-white-50 mb-4">A few quick answers, then we match you with a group that plays when you do.</p>;
  }

  renderBack() {
    const steps = this.steps();
    if (steps.indexOf(this.state.step) <= 0) return null;

    return (
      <button type="button" className="btn btn-link text-white-50 pl-0 mt-3" onClick={this.goBack}>
        &larr; Back
      </button>
    );
  }

  renderGameStep() {
    const matches = this.gameMatches();

    return (
      <div data-signup-step="game">
        <h3>What do you want to play?</h3>
        {this.renderIntro()}
        <input
          type="search"
          className="form-control mb-3"
          placeholder="Search games"
          aria-label="Search games"
          value={this.state.search}
          onChange={(e) => this.setState({ search: e.target.value })}
        />
        <div>
          {matches.map((game) => (
            <button
              type="button"
              key={game.id}
              className="btn btn-outline-light mr-2 mb-2"
              onClick={() => this.pickGame(game)}
            >
              {game.name}
            </button>
          ))}
        </div>
        {matches.length === 0 && <p className="text-white-50">No match. Try a shorter name.</p>}
        {this.renderBack()}
      </div>
    );
  }

  renderGameLine() {
    // Joining a particular session: its game isn't up for change.
    if (!this.state.game || this.props.joining) return null;

    return (
      <p className="text-white-50 mb-3">
        {this.state.game.name}{" "}
        <a href="#" className="text-white-50 small" onClick={this.changeGame}>
          (change game)
        </a>
      </p>
    );
  }

  renderPlatformStep() {
    return (
      <div data-signup-step="platform">
        <h3>What do you play on?</h3>
        {this.renderIntro()}
        {this.renderGameLine()}
        <div>
          {PLATFORMS.map((platform) => (
            <button
              type="button"
              key={platform.value}
              className={"btn mr-2 mb-2 " + platform.value}
              onClick={() => this.pickPlatform(platform.value)}
            >
              {platform.label}
            </button>
          ))}
        </div>
        {this.renderBack()}
      </div>
    );
  }

  renderScheduleStep() {
    const timeZone = browserTimeZone();

    return (
      <div data-signup-step="schedule">
        <h3>When do you usually play?</h3>
        {this.renderIntro()}
        {timeZone && <p className="small text-white-50 mb-3">Your time zone: {timeZone}.</p>}
        <div className="signup-flow__choices">
          {SCHEDULES.map((schedule) => (
            <button
              type="button"
              key={schedule.value}
              className="btn btn-outline-light btn-block text-left mb-2"
              onClick={() => this.pickSchedule(schedule.value)}
            >
              {schedule.label}
            </button>
          ))}
        </div>
        {this.state.errors && (
          <div className="alert alert-danger mt-3" role="alert">
            {this.state.errors}
          </div>
        )}
        {this.renderBack()}
      </div>
    );
  }

  renderAgeStep() {
    return (
      <div data-signup-step="age">
        <h3>What's your age range?</h3>
        {this.renderIntro()}
        <p className="small text-white-50 mb-3">So we match you with players around your age. It stays off your profile.</p>
        <div>
          {AGE_RANGES.map((range) => (
            <button
              type="button"
              key={range.value}
              className="btn btn-outline-light mr-2 mb-2"
              onClick={() => this.pickAge(range.value)}
            >
              {range.label}
            </button>
          ))}
        </div>
        {this.state.errors && (
          <div className="alert alert-danger mt-3" role="alert">
            {this.state.errors}
          </div>
        )}
        {this.renderBack()}
      </div>
    );
  }

  renderAccountStep() {
    const { game, platform, playSchedule, age } = this.state;
    const ageRange = AGE_RANGES.find((range) => range.value === age);
    const summary = [
      game && game.name,
      labelFor(PLATFORMS, platform),
      labelFor(SCHEDULES, playSchedule),
      ageRange && `age ${ageRange.label}`,
    ]
      .filter(Boolean)
      .join(" · ");

    return (
      <div data-signup-step="account">
        <h3>Last step: your account</h3>
        <p className="text-white-50 mb-4">{summary}</p>
        {age === UNDER_18 && (
          <p className="small text-white mb-3" data-signup-guardian>
            If you're under 18, a parent or guardian agrees to our Terms and Privacy Policy on your behalf.
          </p>
        )}

        <form method="post" action={this.props.discordPath} data-signup-discord>
          <input type="hidden" name="authenticity_token" value={this.props.authenticityToken || ""} />
          <input type="hidden" name="game_id" value={game ? game.id : ""} />
          <input type="hidden" name="platform" value={platform} />
          <input type="hidden" name="play_schedule" value={playSchedule} />
          <input type="hidden" name="age" value={age || ""} />
          <input type="hidden" name="time_zone" value={browserTimeZone()} />
          <button type="submit" className="btn btn-lg btn-primary btn-block">
            <i className="fab fa-discord mr-2" aria-hidden="true"></i>
            Continue with Discord
          </button>
          {/* A Discord account is created with the terms accepted
              (User.create_from_discord), so say so before they leave. */}
          <p className="small text-white-50 text-center mt-2 mb-0" data-signup-discord-consent>
            By continuing with Discord you agree to the{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-white-50">
              <u>Terms of Service</u>
            </a>{" "}
            and{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-white-50">
              <u>Privacy Policy</u>
            </a>
            .
          </p>
        </form>

        {!this.props.discordOnly && (
          <form className="mt-4" onSubmit={this.submitEmail} data-signup-email>
            <p className="text-center text-white-50 mb-3">or use your email</p>
            {this.state.errors && (
              <div className="alert alert-danger" role="alert">
                {this.state.errors}
              </div>
            )}
            <div className="form-group">
              <input
                type="text"
                className="form-control"
                placeholder="Username"
                aria-label="Username"
                autoComplete="username"
                value={this.state.gamertag}
                onChange={(e) => this.setState({ gamertag: e.target.value })}
              />
            </div>
            <div className="form-group">
              <input
                type="email"
                className="form-control"
                placeholder="Email"
                aria-label="Email"
                autoComplete="email"
                value={this.state.email}
                onChange={(e) => this.setState({ email: e.target.value })}
              />
            </div>
            <div className="form-group">
              <input
                type="password"
                className="form-control"
                placeholder="Password (6 or more characters)"
                aria-label="Password"
                autoComplete="new-password"
                value={this.state.password}
                onChange={(e) => this.setState({ password: e.target.value })}
              />
            </div>
            <div className="form-group">
              {/* Plain form-check, not custom-checkbox: the theme's page.min.js
                  toggles .custom-checkbox inputs itself on click, behind React. */}
              <div className="form-check">
                <input
                  type="checkbox"
                  id="signup-terms"
                  className="form-check-input"
                  checked={this.state.termsAccepted}
                  onChange={(e) => this.setState({ termsAccepted: e.target.checked })}
                />
                <label className="form-check-label" htmlFor="signup-terms">
                  I agree to the Terms of Service and Privacy Policy
                </label>
              </div>
              <span className="small text-white-50">
                Read the{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-white-50">
                  <u>Terms of Service</u>
                </a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-white-50">
                  <u>Privacy Policy</u>
                </a>
              </span>
            </div>
            {this.props.captchaKey && (
              <div className="form-group">
                <ReCAPTCHA
                  sitekey={this.props.captchaKey}
                  onChange={(value) => this.setState({ captchaToken: value })}
                  theme="dark"
                />
              </div>
            )}
            <button type="submit" className="btn btn-lg btn-outline-light btn-block" disabled={!this.emailReady()}>
              Create account
            </button>
          </form>
        )}
        {this.renderBack()}
      </div>
    );
  }

  render() {
    if (this.state.loading) {
      return (
        <div>
          <p className="lead">{this.props.joining ? "Saving your seat…" : "Finding your group…"}</p>
          <div style={{ minHeight: "300px" }}>
            <Loading />
          </div>
        </div>
      );
    }

    return (
      <div className="signup-flow" data-signup-flow>
        {this.renderContext()}
        {this.state.step === "game" && this.renderGameStep()}
        {this.state.step === "platform" && this.renderPlatformStep()}
        {this.state.step === "schedule" && this.renderScheduleStep()}
        {this.state.step === "age" && this.renderAgeStep()}
        {this.state.step === "account" && this.renderAccountStep()}
      </div>
    );
  }
}
